import ComponentManager from '@/Management/ComponentManager';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import * as Interfaces from '#/DefaultViewExtensionInterfaces';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import ImageViewModelType from '@/ViewModels/Controls/ImageViewModel';
import WindowViewModelType from '@/ViewModels/Controls/WindowViewModel';
import MultilineTextBoxViewModelType from '@/ViewModels/Controls/MultilineTextBoxViewModel';
import ButtonViewModelBuilder from '@/Builder/ButtonViewModelBuilder';
import ImageViewModelBuilder from '@/Builder/ImageViewModelBuilder';
import WindowViewModelBuilder from '@/Builder/WindowViewModelBuilder';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import LocatorHelper from '@/Utility/LocatorHelper';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';

const MULTILINETEXTBOX_SHOWPOPUP = 'MULTILINETEXTBOX_SHOWPOPUP';

export default class MultilineTextBoxPopUpLinkViewModel extends LinkViewModel {
    private MultilineTextBoxViewModel = ComponentManager.getViewModel<typeof MultilineTextBoxViewModelType>('MultilineTextBoxViewModel');
    private value: KnockoutObservable<string>;
    private onOpenCallback: Function;
    private onCloseCallback: Function;
    private isEditingEnabled: KnockoutObservable<boolean>;
    private ergName: string;
    private maxLength: number;
    private parentTooltip: LabelViewModel;

    public constructor(params: any) {
        super(params);

        this.value = this.observableOrDefault(params.value);
        this.isEditingEnabled = this.observableOrDefault(params.isEditingEnabled, false);
        this.ergName = this.paramOrDefault(params.ergName, '');
        this.maxLength = this.paramOrDefault(params.maxLength, 0);
        this.tooltip = new LabelViewModelBuilder().withErgName(MULTILINETEXTBOX_SHOWPOPUP).build();
        this.locator = LocatorHelper.buildLocator(this.parentLocator, 'popup');
        this.parentTooltip = this.paramOrDefault(params.parentTooltip, null);
    }

    public createWindowViewModels(): Interfaces.IMultilineTextBoxPopUpLinkWindowResult {
        const windowLocator = 'window';
        const window = this.createWindowViewModel(windowLocator);
        const textBox = this.createMultilineTextBoxViewModel(windowLocator);
        const buttons = this.createWindowFooterButtonViewModels(window, textBox, windowLocator);

        return {
            window: window,
            textBox: textBox,
            buttons: buttons
        };
    }

    public setOnOpenCallback(func: Function): void {
        this.onOpenCallback = func;
    }

    public setOnCloseCallback(func: Function): void {
        this.onCloseCallback = func;
    }

    private raiseOnOpen(): void {
        if (this.onOpenCallback)
            this.onOpenCallback();
    }

    private raiseOnClose(): void {
        if (this.onCloseCallback)
            this.onCloseCallback();
    }

    /**
    * @override
    */
    public onClick(openInNewWindow?: boolean, event?: JQuery.Event): void {
        this.raiseOnOpen();
    }

    /**
    * @override
    */
    protected createImageViewModel(): ImageViewModelType {
        return new ImageViewModelBuilder()
            .withSource(GlobalConfiguration.inlineImages.TEXTBOX_POPUP)
            .withAlternativeTextErgName(MULTILINETEXTBOX_SHOWPOPUP)
            .build();
    }

    private createMultilineTextBoxViewModel(preLocator: string): MultilineTextBoxViewModelType {
        return new this.MultilineTextBoxViewModel({
            value: this.value(), // Das auflösen der Observable ist hier wichtig, da wird den Wert nicht direkt aktualisieren wollen
            parentLocator: this.locator,
            locator: LocatorHelper.buildLocator(preLocator, 'textbox'),
            maxLength: this.maxLength,
            enablePopUp: false,
            isEnabled: this.isEditingEnabled,
            height: '80%',
            tooltip: this.parentTooltip
        });
    }

    private createWindowViewModel(locator : string): WindowViewModelType {
        return new WindowViewModelBuilder()
            .withHeadlineErgName(this.ergName)
            .updateProperties({
                content: $('<div>'),
                width: '90%',
                height: '80%',
                parentLocator: this.locator,
                locator: locator,
                isModal: true,
                isVisible: true,
                isOpen: true,
                allowClose: true,
                isDraggable: false,
                isResizable: false, // Nach dem Update von Kendo, kann dies auf true gesetzt werden! CF-3178
                })
            .build();
    }

    private createWindowFooterButtonViewModels(window: WindowViewModelType, textBox: MultilineTextBoxViewModelType, preLocator: string) {
        const locator = LocatorHelper.buildLocator(this.locator, preLocator);

        return [
            new ButtonViewModelBuilder()
                .initForOkayButton(locator,
                    () => {
                        this.value(textBox.value());
                        window.isOpen(false);
                        this.raiseOnClose();
                    })
                .withIsEnabled(this.isEditingEnabled)
                .build(),
            new ButtonViewModelBuilder()
                .initForCancelButton(locator,
                    () => {
                        window.isOpen(false);
                        this.raiseOnClose();
                    })
                .build(),
        ];
    }
}

ComponentManager.setViewModel('MultilineTextBoxPopUpLinkViewModel', MultilineTextBoxPopUpLinkViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');